import { Injectable } from '@angular/core';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { IPlannedExecutionApi } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { isValid } from 'date-fns';

interface IUndesignatedExecutionEntry {
  lookupKey: string;
  targetCount: number;
}

interface IUndesignatedExecutionSourceCta {
  ctaId: string | null;
  plannedExecution: IPlannedExecutionApi | null;
}

@Injectable({
  providedIn: 'root',
})
export class CampaignDetailsUndesignatedExecutionOrderService {
  public enrichAnalyticsNodesWithUndesignatedOrders(
    analyticsNodes: IAnalyticsNode[] | null,
    allAnalyticsNodesForOrder?: IAnalyticsNode[] | null,
  ): IAnalyticsNode[] | null {
    if (!analyticsNodes?.length) {
      return analyticsNodes;
    }

    const orderMap = this.buildOrderMap(allAnalyticsNodesForOrder ?? analyticsNodes);

    if (orderMap.size === 0) {
      return analyticsNodes;
    }

    return analyticsNodes.map((analyticsNode) => {
      const lookupKeys = this.getLookupKeysForAnalyticsNode(analyticsNode);
      const undesignatedExecutionOrdersByLookupKey: Record<string, number> = {};

      for (const lookupKey of lookupKeys) {
        const order = orderMap.get(lookupKey);

        if (!order) {
          continue;
        }

        undesignatedExecutionOrdersByLookupKey[lookupKey] = order;
      }

      if (Object.keys(undesignatedExecutionOrdersByLookupKey).length === 0) {
        return analyticsNode;
      }

      return {
        ...analyticsNode,
        undesignatedExecutionOrdersByLookupKey,
      };
    });
  }

  public buildOrderMap(analyticsNodes: IAnalyticsNode[] | null): Map<string, number> {
    if (!analyticsNodes?.length) {
      return new Map<string, number>();
    }

    const unresolvedEntriesByGroup = new Map<string, IUndesignatedExecutionEntry[]>();

    for (const analyticsNode of analyticsNodes) {
      const sourceCtas = this.getSourceCtas(analyticsNode);
      const hasExecutedDate = this.hasExecutedDate(analyticsNode);

      for (const sourceCta of sourceCtas) {
        const resolvedPlannedExecution = sourceCta.plannedExecution ?? analyticsNode.plannedExecution ?? null;

        if (!resolvedPlannedExecution) {
          continue;
        }

        if (resolvedPlannedExecution.executionType !== EExecutionType.undesignated) {
          continue;
        }

        const groupKey = this.getGroupKey(analyticsNode, sourceCta.ctaId, resolvedPlannedExecution);
        const lookupKey = this.getLookupKey(analyticsNode.id, sourceCta.ctaId, resolvedPlannedExecution);
        const targetCount = resolvedPlannedExecution.executionCountTarget ?? 0;

        if (!lookupKey) {
          continue;
        }

        if (hasExecutedDate) {
          continue;
        }

        const groupEntries = unresolvedEntriesByGroup.get(groupKey) ?? [];

        groupEntries.push({
          lookupKey,
          targetCount,
        });
        unresolvedEntriesByGroup.set(groupKey, groupEntries);
      }
    }

    const orderMap = new Map<string, number>();

    for (const groupEntries of unresolvedEntriesByGroup.values()) {
      const unresolvedCount = groupEntries.length;
      const targetCount = Math.max(...groupEntries.map((entry) => entry.targetCount), 0);
      const startOrder = Math.max(targetCount - unresolvedCount + 1, 1);

      groupEntries.forEach((entry, index) => {
        orderMap.set(entry.lookupKey, startOrder + index);
      });
    }

    return orderMap;
  }

  public getLookupKey(
    analyticsNodeId: string,
    ctaId: string | null,
    plannedExecution: IPlannedExecutionApi | null,
  ): string | null {
    if (!plannedExecution) {
      return null;
    }

    if (plannedExecution.executionType !== EExecutionType.undesignated) {
      return null;
    }

    const ctaKey = ctaId ?? 'no-cta';
    const executionKey = this.getExecutionIdentity(plannedExecution);

    return `${analyticsNodeId}::${ctaKey}::${executionKey}`;
  }

  private getSourceCtas(analyticsNode: IAnalyticsNode): IUndesignatedExecutionSourceCta[] {
    const ctas = Array.from(analyticsNode.content?.ctas?.values() ?? []).map((ctaResult) => ({
      ctaId: ctaResult.ctaId,
      plannedExecution: ctaResult.plannedExecution ?? analyticsNode.plannedExecution ?? null,
    }));

    if (ctas.length > 0) {
      return ctas;
    }

    if (!analyticsNode.emptyCtaAnalytics || !analyticsNode.nonExecutedCtaName) {
      return [];
    }

    return [
      {
        ctaId: analyticsNode.nonExecutedCtaId,
        plannedExecution: analyticsNode.plannedExecution ?? null,
      },
    ];
  }

  private hasExecutedDate(analyticsNode: IAnalyticsNode): boolean {
    const executedAt =
      analyticsNode.content?.socialContent?.executionTime ?? this.getValidExecutionDate(analyticsNode.executionTime);

    if (!executedAt) {
      return false;
    }

    return true;
  }

  private getGroupKey(
    analyticsNode: IAnalyticsNode,
    ctaId: string | null,
    plannedExecution: IPlannedExecutionApi,
  ): string {
    const ctaIdentity = ctaId ?? 'no-cta';
    const executionIdentity = this.getExecutionIdentity(plannedExecution);

    return `${analyticsNode.creatorId}::${analyticsNode.platformFeature}::${ctaIdentity}::${executionIdentity}`;
  }

  private getLookupKeysForAnalyticsNode(analyticsNode: IAnalyticsNode): string[] {
    const sourceCtas = this.getSourceCtas(analyticsNode);
    const lookupKeys: string[] = [];

    for (const sourceCta of sourceCtas) {
      const resolvedPlannedExecution = sourceCta.plannedExecution ?? analyticsNode.plannedExecution ?? null;
      const lookupKey = this.getLookupKey(analyticsNode.id, sourceCta.ctaId, resolvedPlannedExecution);

      if (!lookupKey) {
        continue;
      }

      lookupKeys.push(lookupKey);
    }

    return lookupKeys;
  }

  private getExecutionIdentity(plannedExecution: IPlannedExecutionApi): string {
    return (
      plannedExecution.ctaExecutionId ??
      `${plannedExecution.ctaId ?? 'no-cta-id'}::${plannedExecution.plannedStartDate ?? 'no-date'}::${plannedExecution.plannedStartTime ?? 'no-time'}::${plannedExecution.executionCountTarget ?? 0}`
    );
  }

  private getValidExecutionDate(executionTime: string | null): string | null {
    if (!executionTime) {
      return null;
    }

    const parsedDate = new Date(executionTime);

    if (!isValid(parsedDate)) {
      return null;
    }

    return executionTime;
  }
}
